/**
 * Structured Logging Utility with Correlation IDs
 *
 * This module provides structured logging for the frontend with correlation ID tracking
 * to enable distributed request tracing across frontend and Edge Functions.
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/lib/logger'
 *
 * logger.info('User logged in', { userId: '123' })
 * logger.error('Failed to transform text', { error, tone: 'professional' })
 * ```
 */

import { v4 as uuidv4 } from 'uuid'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogEntry {
  timestamp: string
  level: LogLevel
  correlation_id: string
  user_id?: string
  message: string
  metadata?: Record<string, unknown>
  error?: {
    name: string
    message: string
    stack?: string
  }
}

class Logger {
  private correlationId: string | null = null
  private userId: string | null = null
  private logLevel: LogLevel

  constructor() {
    // Set log level based on environment
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined
    const mode = import.meta.env.MODE
    this.logLevel = envLogLevel || (mode === 'production' ? 'info' : 'debug')
  }

  /**
   * Generate a new correlation ID for tracking a request flow
   */
  generateCorrelationId(): string {
    this.correlationId = uuidv4()
    return this.correlationId
  }

  /**
   * Get the current correlation ID
   */
  getCorrelationId(): string | null {
    return this.correlationId
  }

  /**
   * Set the correlation ID (useful for resuming a tracked flow)
   */
  setCorrelationId(correlationId: string): void {
    this.correlationId = correlationId
  }

  /**
   * Clear the correlation ID
   */
  clearCorrelationId(): void {
    this.correlationId = null
  }

  /**
   * Set the current user ID for logging context
   */
  setUserId(userId: string | null): void {
    this.userId = userId
  }

  /**
   * Get the current user ID
   */
  getUserId(): string | null {
    return this.userId
  }

  /**
   * Check if a log level should be logged based on current log level
   */
  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error']
    const currentLevelIndex = levels.indexOf(this.logLevel)
    const messageLevelIndex = levels.indexOf(level)
    return messageLevelIndex >= currentLevelIndex
  }

  /**
   * Create a structured log entry
   */
  private createLogEntry(
    level: LogLevel,
    message: string,
    metadata?: Record<string, unknown>,
    error?: Error
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      correlation_id: this.correlationId || 'no-correlation-id',
      message,
    }

    if (this.userId) {
      entry.user_id = this.userId
    }

    if (metadata) {
      entry.metadata = metadata
    }

    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      }
    }

    return entry
  }

  /**
   * Output log entry to console
   */
  private output(entry: LogEntry): void {
    // In production, this could send to a centralized logging service
    // For now, we output to console with structured format

    const consoleMethod =
      entry.level === 'error' ? console.error : entry.level === 'warn' ? console.warn : console.log

    const mode = import.meta.env.MODE
    if (mode === 'development') {
      // Pretty print in development
      consoleMethod(
        `[${entry.level.toUpperCase()}] [${entry.correlation_id.slice(0, 8)}] ${entry.message}`,
        entry.metadata || '',
        entry.error || ''
      )
    } else {
      // Structured JSON in production
      consoleMethod(JSON.stringify(entry))
    }
  }

  /**
   * Log a debug message
   */
  debug(message: string, metadata?: Record<string, unknown>): void {
    if (!this.shouldLog('debug')) return
    const entry = this.createLogEntry('debug', message, metadata)
    this.output(entry)
  }

  /**
   * Log an info message
   */
  info(message: string, metadata?: Record<string, unknown>): void {
    if (!this.shouldLog('info')) return
    const entry = this.createLogEntry('info', message, metadata)
    this.output(entry)
  }

  /**
   * Log a warning message
   */
  warn(message: string, metadata?: Record<string, unknown>): void {
    if (!this.shouldLog('warn')) return
    const entry = this.createLogEntry('warn', message, metadata)
    this.output(entry)
  }

  /**
   * Log an error message
   */
  error(message: string, error?: Error, metadata?: Record<string, unknown>): void {
    if (!this.shouldLog('error')) return
    const entry = this.createLogEntry('error', message, metadata, error)
    this.output(entry)
  }

  /**
   * Create a child logger with a specific context
   */
  child(_context: Record<string, unknown>): Logger {
    const childLogger = new Logger()
    childLogger.correlationId = this.correlationId
    childLogger.userId = this.userId
    // Child logger would merge context into all log entries
    // Implementation simplified for now
    return childLogger
  }
}

// Export singleton instance
export const logger = new Logger()

// Export correlation ID header constant for use in API calls
export const CORRELATION_ID_HEADER = 'X-Correlation-ID'
